// client/src/pages/seo/DigitalMenuPizzeriaEn.jsx
import React, { useEffect } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "A pizzeria needs a menu that handles variants clearly",
  },
  {
    id: "why",
    label: "Why does a digital menu make sense for pizzerias?",
  },
  {
    id: "structure",
    label: "How should a digital menu for a pizzeria be structured?",
  },
  {
    id: "sizes",
    label: "How should pizza sizes, extras and toppings be presented?",
  },
  {
    id: "photos",
    label: "Should a pizzeria use pictures in the digital menu?",
  },
  {
    id: "mistakes",
    label: "What mistakes happen with digital menus for pizzerias?",
  },
  {
    id: "recommendation",
    label: "How would I start as a pizzeria owner?",
  },
  {
    id: "next-steps",
    label: "What should you do now?",
  },
  {
    id: "bestplace",
    label: "How Best Place helps you create a digital pizzeria menu",
  },
  {
    id: "faq",
    label: "FAQ about digital menus for pizzerias",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitalMenuPizzeriaEn() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Digital Menu for Pizzerias: Pizza Sizes, Toppings and Offers Online | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/digital-menu-pizzeria",
          de: "/de/digitale-speisekarte-pizzeria",
          no: "/no/digital-meny-pizzeria",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Digital menu for pizzerias: manage pizza sizes, toppings and
              offers online
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                A pizzeria often has more variants than a standard restaurant
                menu: different pizza sizes, extra toppings, vegetarian options,
                family pizza, lunch offers, pasta, salads, desserts and drinks.
                That is why a digital menu can be especially useful here.
              </p>

              <p className="seoHero__text">
                Instead of maintaining several PDF files, printed flyers or
                outdated menu cards, the pizzeria can update dishes, prices,
                extras and offers online. The QR code stays the same while the
                menu behind it remains current.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>A pizzeria needs a menu that handles variants clearly</h2>

          <p>
            For a pizzeria, it is often not enough to place a simple list of
            dishes online. Guests want to know: which size is available? Which
            toppings can be added? Are there vegetarian options? Can ingredients
            be changed? Which offers are available today?
          </p>

          <p>
            A digital pizzeria menu should make these options easier to
            understand. It should help guests find pizza, pasta, drinks, offers,
            sizes and extras without reading a crowded PDF or outdated flyer.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Weaker</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Long PDF menu with tiny price tables.
                </li>
                <li className="text__seo-li">
                  Sizes and extras are hard to understand.
                </li>
                <li className="text__seo-li">Old flyers stay online.</li>
                <li className="text__seo-li">
                  Unavailable products remain visible.
                </li>
                <li className="text__seo-li">
                  Lunch offers are not updated.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Better</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Clear categories for pizza, pasta, salads and drinks.
                </li>
                <li className="text__seo-li">
                  Sizes and extras displayed clearly.
                </li>
                <li className="text__seo-li">Prices updated online.</li>
                <li className="text__seo-li">
                  Unavailable dishes can be hidden.
                </li>
                <li className="text__seo-li">QR code stays the same.</li>
              </ul>
            </div>
          </div>

          <h3>Start with a small digital pizza menu</h3>

          <p>
            Add your most popular pizzas, sizes and a few extras first. Then
            check the smartphone view through the QR code. A small realistic
            test is better than trying to digitize every variant from the first
            day.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why does a digital menu make sense for pizzerias?</h2>

            <p>
              Pizzerias often have a specific menu structure. One pizza can be
              offered in several sizes, toppings can be added, some ingredients
              cost extra and certain dishes may be available only at specific
              times.
            </p>

            <p>
              On paper or in a PDF, this structure can quickly become crowded.
              In a digital menu, categories, variants and offers can be
              separated more clearly.
            </p>

            <h3>Digital structure helps with pizzeria complexity</h3>

            <p>
              A digital menu is not just a modern presentation. It helps make
              typical pizzeria complexity easier for guests to understand:
              sizes, extras, toppings, offers, vegetarian options and
              availability.
            </p>

            <p>
              It also helps the pizzeria keep prices and offers current without
              constantly replacing files, flyers or printed menu cards.
            </p>
          </div>
        </section>

        <section id="structure" className="acquaintance">
          <h2>How should a digital menu for a pizzeria be structured?</h2>

          <p>
            A good pizzeria menu needs clear categories. When pizza, pasta,
            salads, desserts, drinks, offers and extras appear in one long list,
            guests quickly lose orientation.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Classic pizzas.</li>
            <li className="text__seo-li">Specialty pizzas.</li>
            <li className="text__seo-li">Vegetarian pizza.</li>
            <li className="text__seo-li">Pasta.</li>
            <li className="text__seo-li">Salads.</li>
            <li className="text__seo-li">Desserts.</li>
            <li className="text__seo-li">Drinks.</li>
            <li className="text__seo-li">Lunch offers.</li>
          </ul>

          <p>
            The order matters. Bestsellers and simple categories should be
            visible first. Extras and special options should not interrupt the
            main menu flow.
          </p>

          <VisualIdea>
            Smartphone with pizzeria menu: classic pizzas, specialties, pasta,
            salads, drinks and a visible section for offers.
          </VisualIdea>
        </section>

        <section id="sizes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>How should pizza sizes, extras and toppings be presented?</h2>

            <p>
              The biggest challenge in pizzeria menus is often variants: small,
              regular, large, family size, extra cheese, additional toppings,
              spicy option, vegetarian option or ingredients removed.
            </p>

            <p>
              Guests should understand these options quickly without reading a
              complicated price table.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Name sizes clearly:</strong> For example 26 cm, 32 cm or
                family pizza.
              </li>

              <li className="text__seo-li">
                <strong>Keep price logic simple:</strong> Variants should not
                feel like hidden costs.
              </li>

              <li className="text__seo-li">
                <strong>Explain extras separately:</strong> Extra cheese,
                vegetables, meat or sauce should stay easy to understand.
              </li>

              <li className="text__seo-li">
                <strong>Show popular options:</strong> For example vegetarian,
                spicy or extra cheese.
              </li>

              <li className="text__seo-li">
                <strong>Manage availability:</strong> If a topping is
                unavailable, it should not remain prominent.
              </li>
            </ol>

            <h3>The more variants, the clearer the structure must be</h3>

            <p>
              If one dish has many possible choices, the digital menu should not
              hide this information in a confusing text block. Sizes, extras and
              availability should be easy to scan and understand.
            </p>
          </div>
        </section>

        <section id="photos" className="acquaintance">
          <h2>Should a pizzeria use pictures in the digital menu?</h2>

          <p>
            Pictures can be very helpful for pizzerias, but not every pizza
            needs a photo immediately. Good pictures are especially useful for
            specialties, new offers, desserts or dishes that guests may not know
            well.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Show bestsellers.</li>
            <li className="text__seo-li">Highlight specialty pizza.</li>
            <li className="text__seo-li">Present desserts.</li>
            <li className="text__seo-li">Make lunch offers visible.</li>
            <li className="text__seo-li">Show realistic portions.</li>
            <li className="text__seo-li">Use consistent photo style.</li>
            <li className="text__seo-li">Avoid very large files.</li>
            <li className="text__seo-li">Replace old photos.</li>
          </ul>

          <p>
            For a pizzeria, a realistic photo is often stronger than a perfect
            advertising image. Guests should be able to expect something close
            to what later arrives on the plate or in the box.
          </p>

          <VisualIdea>
            A digital pizzeria menu with three selected images: a bestseller, a
            specialty pizza and a dessert. The photos are bright, realistic and
            consistent.
          </VisualIdea>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What mistakes happen with digital menus for pizzerias?</h2>

            <p>
              Many mistakes happen when a digital menu is treated like an old
              flyer. A pizzeria needs a clear digital structure so variants and
              extras stay understandable.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common mistakes</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    All pizzas appear in one long list.
                  </li>
                  <li className="text__seo-li">
                    Sizes and prices are hard to read.
                  </li>
                  <li className="text__seo-li">
                    Extras are unclear or explained too late.
                  </li>
                  <li className="text__seo-li">
                    Old flyer PDFs remain online.
                  </li>
                  <li className="text__seo-li">
                    Unavailable toppings remain visible.
                  </li>
                  <li className="text__seo-li">
                    Photos do not match the real pizza.
                  </li>
                  <li className="text__seo-li">
                    Lunch offers are not updated.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">Use clear categories.</li>
                  <li className="text__seo-li">
                    Present sizes logically.
                  </li>
                  <li className="text__seo-li">
                    Explain extras separately and clearly.
                  </li>
                  <li className="text__seo-li">
                    Maintain one central digital menu.
                  </li>
                  <li className="text__seo-li">
                    Hide unavailable ingredients.
                  </li>
                  <li className="text__seo-li">
                    Use realistic pictures.
                  </li>
                  <li className="text__seo-li">
                    Update offers regularly.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>How would I start as a pizzeria owner?</h2>

          <p>
            I would not try to map the entire menu with every variant perfectly
            from day one. I would start with the core offer and expand the
            structure afterwards.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Start with bestsellers:</strong> Add 10 to 15 popular
              pizzas.
            </li>

            <li className="text__seo-li">
              <strong>Define sizes clearly:</strong> For example regular, large
              and family size.
            </li>

            <li className="text__seo-li">
              <strong>Limit extras first:</strong> Add only the most important
              extra options at the beginning.
            </li>

            <li className="text__seo-li">
              <strong>Test one offer category:</strong> For example lunch menu
              or family offer.
            </li>

            <li className="text__seo-li">
              <strong>Check on smartphone:</strong> Guests should quickly
              understand what they can choose.
            </li>

            <li className="text__seo-li">
              <strong>Expand later:</strong> Add pasta, salads, desserts and
              drinks afterwards.
            </li>
          </ol>

          <p>
            This keeps the digital menu clear and lets it grow from the real
            restaurant workflow.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What should you do now?</h2>

            <p>
              If you want to create a digital menu for your pizzeria, start with
              a small realistic test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Create a digital menu.</li>
              <li className="text__seo-li">
                Add categories for pizza, pasta and drinks.
              </li>
              <li className="text__seo-li">
                Enter 10 to 15 popular pizzas.
              </li>
              <li className="text__seo-li">
                Add sizes and the most important extras.
              </li>
              <li className="text__seo-li">
                Open the menu on a smartphone.
              </li>
              <li className="text__seo-li">
                Test the QR code inside the restaurant.
              </li>
              <li className="text__seo-li">
                Expand offers, photos and further categories afterwards.
              </li>
            </ul>

            <VisualIdea>
              A small pizzeria test menu: 10 popular pizzas, clear sizes, a few
              extras, one offer category and a smartphone view opened through
              the QR code.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ about digital menus for pizzerias</h2>

          <div className="seoFaq">
            <details>
              <summary>What is a digital menu for pizzerias?</summary>
              <p>
                It is an online-managed menu for pizza, pasta, salads, drinks,
                offers, sizes and extras that guests can open by QR code.
              </p>
            </details>

            <details>
              <summary>Can I show pizza sizes?</summary>
              <p>
                Yes. Sizes such as small, regular, large or family size can be
                displayed clearly with matching prices.
              </p>
            </details>

            <details>
              <summary>Can I show extras and additional toppings?</summary>
              <p>
                Yes. Extras should be shown clearly and not become too
                complicated, so guests understand what is possible.
              </p>
            </details>

            <details>
              <summary>
                Do I need to reprint the QR code when prices change?
              </summary>
              <p>
                No, if the QR code points to a stable menu link. Prices can be
                updated online while the QR code stays the same.
              </p>
            </details>

            <details>
              <summary>Should every pizza have a picture?</summary>
              <p>
                Not necessarily. For the first step, pictures for bestsellers,
                specialties, offers or new dishes are enough.
              </p>
            </details>

            <details>
              <summary>Can I start for free?</summary>
              <p>
                Yes. For a first test, a few popular pizzas, sizes, prices and a
                QR code check on a smartphone are enough.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="no"
          group="menu"
          exclude="/no/digital-meny-pizzeria"
        />
      </article>
    </main>
  );
}